import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import './NotificationBell.css';

function timeAgo(dateStr) {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// Where a click on a given notification should take you.
function resolveLink(n) {
  if (n.relatedType === 'TICKET') return `/tickets/${n.relatedId}`;
  if (n.relatedType === 'BLOG') {
    // Admins reviewing a member's draft go to blog management;
    // everyone else (blog published) goes to the public blog list.
    return n.type === 'BLOG_DRAFTED' ? '/admin/blogs' : '/blogs';
  }
  return null;
}

export default function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleItemClick = (n) => {
    if (!n.isRead) markAsRead(n.id);
    const link = resolveLink(n);
    setOpen(false);
    if (link) navigate(link);
  };

  return (
    <div className="notif-bell" ref={rootRef}>
      <button
        type="button"
        className="notif-bell__btn"
        aria-label="Notifications"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="notif-bell__icon"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="notif-bell__badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="notif-bell__dropdown">
          <div className="notif-bell__header">
            <span>Notifications</span>
            {unreadCount > 0 && (
              <button type="button" className="notif-bell__mark-all" onClick={markAllAsRead}>
                Mark all read
              </button>
            )}
          </div>

          <div className="notif-bell__list">
            {notifications.length === 0 ? (
              <div className="notif-bell__empty">No notifications yet</div>
            ) : (
              notifications.slice(0, 20).map((n) => (
                <button
                  type="button"
                  key={n.id}
                  className={`notif-bell__item ${n.isRead ? '' : 'notif-bell__item--unread'}`}
                  onClick={() => handleItemClick(n)}
                >
                  <span className="notif-bell__item-title">{n.title}</span>
                  <span className="notif-bell__item-message">{n.message}</span>
                  <span className="notif-bell__item-time">{timeAgo(n.createdAt)}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}